import React, {
  PropsWithChildren,
  forwardRef,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { ModalProps } from "react-bootstrap";
import {
  StreakConfig,
  StreakStatus,
  StreakConfigList,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./StreakModal.scss";
import FlameIcon from "../../../icons/FlameIcon";
import StreakDays from "../../StreakDays/StreakDays";
import Gem from "../../../Gem/Gem";
import StepIndicator from "../../../StepIndicator/StepIndicator";
import { formatCurrency } from "../../../../utils/helpers";
import BottomDrawer from "../../../BottomDrawer/BottomDrawer";
import useAuth from "../../../../hooks/useAuth";

interface StreakModalProps extends ModalProps {
  status: StreakStatus;
  config: StreakConfigList;
  onHide: () => void;
}
interface RewardScreenProps {
  status: StreakStatus;
  dailyRewards: IDailyReward[];
}

interface IDailyReward extends StreakConfig {
  title: string;
}

function useGenerateDailyRewards() {
  const { t } = useTranslation("common");

  const generateDailyRewards = useCallback(
    (configList: StreakConfigList): IDailyReward[] => {
      const STREAK_DAYS_MAX = 15;

      return configList.records
        .map((config) => {
          const { milestone } = config;

          if (milestone <= STREAK_DAYS_MAX - 2)
            return { ...config, title: `${t("day")} ${milestone}` };
          else if (milestone === STREAK_DAYS_MAX - 1)
            return { ...config, title: t("jackpot") };
          else if (milestone === STREAK_DAYS_MAX)
            return { ...config, title: t("double_jackpot") };
          else return { ...config, title: "" };
        })
        .filter((reward) => reward.milestone <= STREAK_DAYS_MAX);
    },
    [t]
  );

  return { generateDailyRewards };
}

function DailyReward({
  title,
  amount,
  isMiddle,
  isActive,
  isCompleted,
}: {
  title: string;
  amount: number;
  isMiddle: boolean;
  isActive: boolean;
  isCompleted: boolean;
}) {
  return (
    <div className="daily-reward" data-middle={isMiddle}>
      <div
        key={title}
        className={`day ${isActive ? "active-day" : ""} ${
          isCompleted ? "completed-day" : ""
        }`}
        data-middle={isMiddle}
      >
        <p className="label">{title}</p>
        <p>
          <Gem height={20} width={20} />{" "}
          <span className="fw-bold">{amount}</span>{" "}
        </p>
      </div>
    </div>
  );
}

function RewardsRow({ children }: PropsWithChildren) {
  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
      {children}
    </div>
  );
}

const checkIsActiveMilestone = (milestone: number, currentCount: number) =>
  milestone === currentCount + 1;

const checkIsCompletedMilestone = (milestone: number, currentCount: number) =>
  milestone < currentCount + 1;

const ScreenOne = forwardRef<HTMLDivElement, RewardScreenProps>(
  function ScreenOne({ status, dailyRewards, ...restProps }, ref) {
    return (
      <div className="days-container" ref={ref} {...restProps}>
        <RewardsRow>
          {dailyRewards
            .filter((reward) => reward.milestone < 4)
            .map((reward, i) => (
              <DailyReward
                key={reward.title}
                isMiddle={i === 1}
                isActive={checkIsActiveMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                isCompleted={checkIsCompletedMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                {...reward}
              />
            ))}
        </RewardsRow>
        <RewardsRow>
          {dailyRewards
            .filter((reward) => reward.milestone > 3 && reward.milestone < 7)
            .map((reward, i) => (
              <DailyReward
                key={reward.title}
                isMiddle={i === 1}
                isActive={checkIsActiveMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                isCompleted={checkIsCompletedMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                {...reward}
              />
            ))}
        </RewardsRow>
        <div
          className={`goal-day ${
            checkIsActiveMilestone(
              dailyRewards[6].milestone,
              status.streakCount
            )
              ? "active-day"
              : ""
          } ${
            checkIsCompletedMilestone(
              dailyRewards[6].milestone,
              status.streakCount
            )
              ? "completed-day"
              : ""
          }`}
        >
          <p className="title">{dailyRewards[6].title}</p>
          <p className="reward">
            <Gem width={20} height={20} /> <span>{dailyRewards[6].amount}</span>
          </p>
          <Gem className="gem gem-1" />
          <Gem className="gem gem-2" />
        </div>
      </div>
    );
  }
);

const ScreenTwo = forwardRef<HTMLDivElement, RewardScreenProps>(
  function ScreenTwo({ status, dailyRewards, ...restProps }, ref) {
    return (
      <div className="days-container" ref={ref} {...restProps}>
        <RewardsRow>
          {dailyRewards
            .filter((reward) => reward.milestone > 7 && reward.milestone < 11)
            .map((reward, i) => (
              <DailyReward
                key={reward.title}
                isMiddle={i === 1}
                isActive={checkIsActiveMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                isCompleted={checkIsCompletedMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                {...reward}
              />
            ))}
        </RewardsRow>
        <RewardsRow>
          {dailyRewards
            .filter((reward) => reward.milestone > 10 && reward.milestone < 14)
            .map((reward, i) => (
              <DailyReward
                key={reward.title}
                isMiddle={i === 1}
                isActive={checkIsActiveMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                isCompleted={checkIsCompletedMilestone(
                  reward.milestone,
                  status.streakCount
                )}
                {...reward}
              />
            ))}
        </RewardsRow>
        <div
          className={`goal-day ${
            checkIsActiveMilestone(
              dailyRewards[13].milestone,
              status.streakCount
            )
              ? "active-day"
              : ""
          } ${
            checkIsCompletedMilestone(
              dailyRewards[13].milestone,
              status.streakCount
            )
              ? "completed-day"
              : ""
          }`}
        >
          <p className="title">{dailyRewards[13].title}</p>
          <p className="reward">
            <Gem width={20} height={20} />{" "}
            <span>{dailyRewards[13].amount}</span>
          </p>
          <Gem className="gem gem-1" />
          <Gem className="gem gem-2" />
          <Gem className="gem gem-3" />
          <Gem className="gem gem-4" />
        </div>
      </div>
    );
  }
);

const ScreenThree = forwardRef<HTMLDivElement, RewardScreenProps>(
  function ScreenThree({ dailyRewards, status, ...restProps }, ref) {
    return (
      <div className="days-container" ref={ref} {...restProps}>
        <div
          className={`double-jackpot ${
            checkIsActiveMilestone(
              dailyRewards[14].milestone,
              status.streakCount
            )
              ? "active-day"
              : ""
          }`}
        >
          <p className="title">{dailyRewards[14].title}</p>
          <p className="reward">
            <Gem width={20} height={20} />{" "}
            {formatCurrency(dailyRewards[14].amount)}
          </p>
        </div>
      </div>
    );
  }
);

function StreakModal({ show, onHide, status, config }: StreakModalProps) {
  const { t } = useTranslation();
  const [visibleItem, setVisibleItem] = useState(0);
  const days = status.streakCount;
  const { userProfile } = useAuth();
  const username = userProfile?.username || "";
  const { generateDailyRewards } = useGenerateDailyRewards();
  const headingText = days ? `${days} ${t("day_streak")}` : t("start_streak");
  const subHeadingText = days
    ? `${t("way_to_go")}, ${username}`
    : t("play_a_game_to_start_a_streak");

  const item1Ref = useRef<HTMLDivElement | null>(null);
  const item2Ref = useRef<HTMLDivElement | null>(null);
  const item3Ref = useRef<HTMLDivElement | null>(null);
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);
  const dailyRewards = useMemo(
    () => generateDailyRewards(config),
    [config, generateDailyRewards]
  );

  useEffect(() => {
    const myItem1Ref = item1Ref.current;
    const myItem2Ref = item2Ref.current;
    const myItem3Ref = item3Ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === myItem1Ref) {
              setVisibleItem(0);
            } else if (entry.target === myItem2Ref) {
              setVisibleItem(1);
            } else if (entry.target === myItem3Ref) {
              setVisibleItem(2);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (myItem1Ref) observer.observe(myItem1Ref);
    if (myItem2Ref) observer.observe(myItem2Ref);
    if (myItem3Ref) observer.observe(myItem3Ref);

    return () => {
      if (myItem1Ref) observer.unobserve(myItem1Ref);
      if (myItem2Ref) observer.unobserve(myItem2Ref);
      if (myItem3Ref) observer.unobserve(myItem3Ref);
    };
  }, [status.streakCount]);

  useEffect(() => {
    const myItem1Ref = item1Ref.current;
    const myItem2Ref = item2Ref.current;
    const myItem3Ref = item3Ref.current;

    if (!myItem1Ref || !myItem2Ref || !myItem3Ref) return;

    const timeoutId1 = setTimeout(() => {
      myItem2Ref.scrollIntoView();
    }, 500);

    const timeoutId2 = setTimeout(() => {
      myItem1Ref.scrollIntoView();
    }, 1000);

    const timeoutId3 = setTimeout(() => {
      if (status.streakCount >= 14) {
        setVisibleItem(3);
        myItem3Ref.scrollIntoView();
      } else if (status.streakCount > 7) {
        setVisibleItem(2);
        myItem2Ref.scrollIntoView();
      }
    }, 1500);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [status.streakCount]);

  return (
    <BottomDrawer onClose={onHide} isOpen={!!show}>
      <div className="streak-info-modal">
        <div className="top-row position-relative d-flex justify-content-center">
          <span className="flame-container">
            <FlameIcon />
          </span>
        </div>

        <h2>{headingText}</h2>
        <p className="way-to-go-text">{subHeadingText}!</p>
        <StreakDays className="mb-3" status={status} />
        <div className="rewards-container">
          <h5>{t("rewards")}</h5>
          <div
            className="screens-container scrollbar-hidden d-flex overflow-auto gap-2"
            ref={itemsContainerRef}
          >
            <ScreenOne
              ref={item1Ref}
              status={status}
              dailyRewards={dailyRewards}
            />
            <ScreenTwo
              ref={item2Ref}
              status={status}
              dailyRewards={dailyRewards}
            />
            <ScreenThree
              ref={item3Ref}
              status={status}
              dailyRewards={dailyRewards}
            />
          </div>

          <StepIndicator currentStep={visibleItem} className="mt-2 m-auto" />
        </div>
      </div>
    </BottomDrawer>
  );
}

export default StreakModal;
